import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FaTrash, FaEdit, FaCheck, FaTimes, FaPlus } from "react-icons/fa";
import api from "../../../services/api";
import Select from "../../../components/Select";

export default function KpisDashboard({ user }) {
  const [weeklyKpis, setWeeklyKpis] = useState([]);
  const [weeklyDataStructure, setWeeklyDataStructure] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [showAddKpi, setShowAddKpi] = useState(false);
  const [newKpiLabel, setNewKpiLabel] = useState("");
  const [newKpiDescription, setNewKpiDescription] = useState("");
  const [newKpiTarget, setNewKpiTarget] = useState("");
  const [newKpiUnit, setNewKpiUnit] = useState("integer");
  const [editingKpiId, setEditingKpiId] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  // Fetch KPIs from backend
  const fetchKpis = async () => {
    try {
      const { data, ok } = await api.post("/mgt_kpi/search", { user_id: user._id, limit: 10 });

      if (!ok) return toast.error("Error fetching KPIs");

      if (data.length === 0) {
        setWeeklyKpis([]);
        setWeeklyDataStructure([]);
        return;
      }

      const queryParams = selectedYear && selectedMonth ? `?year=${selectedYear}&month=${selectedMonth}` : "";
      const firstKpiResponse = await api.get(`/mgt_kpi_measurement/kpi/${data[0]._id}/weekly-data${queryParams}`);

      if (firstKpiResponse.ok && firstKpiResponse.data) {
        setWeeklyDataStructure(firstKpiResponse.data);
      }

      const kpisWithData = await Promise.all(
        data.map(async (kpi) => {
          const measurements = await fetchKpiMeasurements(kpi._id);
          return {
            ...kpi,
            weeklyData: measurements.weeklyData,
            structure: measurements.structure,
          };
        }),
      );
      setWeeklyKpis(kpisWithData);
    } catch (error) {
      toast.error("Error fetching KPIs");
    }
  };

  // Fetch measurements for a specific KPI using new weekly-data endpoint
  const fetchKpiMeasurements = async (kpiId) => {
    try {
      const queryParams = selectedYear && selectedMonth ? `?year=${selectedYear}&month=${selectedMonth}` : "";
      const { data, ok } = await api.get(`/mgt_kpi_measurement/kpi/${kpiId}/weekly-data${queryParams}`);
      if (!ok) return { weeklyData: [], structure: [] };

      return {
        weeklyData: data.map((week) => week.value),
        structure: data.map((week) => ({ ...week })),
      };
    } catch (error) {
      console.error("Error fetching measurements:", error);
      return { weeklyData: [], structure: [] };
    }
  };

  // Add new KPI
  const addKpi = async () => {
    if (!newKpiLabel.trim() || !newKpiTarget.trim()) {
      toast.error("Please fill in KPI name and target");
      return;
    }

    try {
      const { data, ok } = await api.post("/mgt_kpi", {
        user_id: user._id,
        user_name: user.name,
        name: newKpiLabel.trim(),
        description: newKpiDescription.trim(),
        target: parseFloat(newKpiTarget),
        unit: newKpiUnit,
      });

      if (!ok) return toast.error("Error adding KPI");

      setNewKpiLabel("");
      setNewKpiDescription("");
      setNewKpiTarget("");
      setNewKpiUnit("integer");
      setShowAddKpi(false);
      fetchKpis();
      toast.success("KPI added successfully");
    } catch (error) {
      toast.error("Error adding KPI");
    }
  };

  // Start editing KPI (prefill form)
  const startEditKpi = (kpi) => {
    setEditingKpiId(kpi._id);
    setNewKpiLabel(kpi.name || "");
    setNewKpiDescription(kpi.description || "");
    setNewKpiTarget((kpi.target != null ? kpi.target : "").toString());
    setNewKpiUnit(kpi.unit || "integer");
    setShowAddKpi(true);
  };

  // Submit edit KPI
  const submitEditKpi = async () => {
    if (!editingKpiId) return;
    if (!newKpiLabel.trim() || !newKpiTarget.toString().trim()) {
      toast.error("Please fill in KPI name and target");
      return;
    }

    const updates = {
      name: newKpiLabel.trim(),
      description: newKpiDescription.trim(),
      target: parseFloat(newKpiTarget),
      unit: newKpiUnit,
    };

    await updateKpi(editingKpiId, updates);

    setEditingKpiId(null);
    setNewKpiLabel("");
    setNewKpiDescription("");
    setNewKpiTarget("");
    setNewKpiUnit("integer");
    setShowAddKpi(false);
    fetchKpis();
  };

  // Update KPI
  const updateKpi = async (kpiId, updates) => {
    try {
      const { data, ok } = await api.put(`/mgt_kpi/${kpiId}`, updates);
      if (!ok) return toast.error("Error updating KPI");
      setWeeklyKpis((prev) => prev.map((kpi) => (kpi._id === kpiId ? { ...kpi, ...data } : kpi)));
      toast.success("KPI updated successfully");
    } catch (error) {
      toast.error("Error updating KPI");
    }
  };

  // Delete KPI
  const deleteKpi = async (kpiId) => {
    try {
      const { ok } = await api.remove(`/mgt_kpi/${kpiId}`);
      if (!ok) return toast.error("Error deleting KPI");

      setWeeklyKpis((prev) => prev.filter((kpi) => kpi._id !== kpiId));
      toast.success("KPI deleted successfully");
    } catch (error) {
      toast.error("Error deleting KPI");
    }
  };

  const updateMeasurement = async (kpiId, weekIndex, value) => {
    try {
      const weekStructure = weeklyDataStructure[weekIndex];
      if (!weekStructure) {
        toast.error("Week structure not found");
        return;
      }

      const { year, week, month } = weekStructure;
      const { data: existingMeasurements } = await api.get(`/mgt_kpi_measurement/kpi/${kpiId}?year=${year}&week=${week}`);

      if (existingMeasurements && existingMeasurements.length > 0) {
        const measurementId = existingMeasurements[0]._id;
        await api.put(`/mgt_kpi_measurement/${measurementId}`, { value });
      } else {
        const kpi = weeklyKpis.find((k) => k._id === kpiId);
        await api.post("/mgt_kpi_measurement", {
          kpi_id: kpiId,
          kpi_name: kpi.name,
          value: value,
          year: year,
          week: week,
          month: month,
          date: new Date(),
        });
      }

      setWeeklyKpis((prev) =>
        prev.map((kpi) => {
          if (kpi._id === kpiId) {
            const newData = [...kpi.weeklyData];
            newData[weekIndex] = value;
            return { ...kpi, weeklyData: newData };
          }
          return kpi;
        }),
      );

      toast.success("Measurement updated successfully");
    } catch (error) {
      toast.error("Error updating measurement");
    }
  };

  const updateTarget = async (kpiId, weekIndex, target) => {
    try {
      const weekStructure = weeklyDataStructure[weekIndex];
      if (!weekStructure) {
        toast.error("Week structure not found");
        return;
      }

      const { year, week, month } = weekStructure;
      const { data: existingMeasurements } = await api.get(`/mgt_kpi_measurement/kpi/${kpiId}?year=${year}&week=${week}`);

      if (existingMeasurements && existingMeasurements.length > 0) {
        // Mettre à jour la target de la mesure existante
        const measurementId = existingMeasurements[0]._id;

        const { data, ok } = await api.put(`/mgt_kpi_measurement/${measurementId}`, { target: parseFloat(target) });
        if (!ok) {
          toast.error("Error updating target");
          return;
        }
      } else {
        // Créer une nouvelle mesure avec la target
        const kpi = weeklyKpis.find((k) => k._id === kpiId);
        const { data, ok } = await api.post("/mgt_kpi_measurement", {
          kpi_id: kpiId,
          kpi_name: kpi.name,
          value: 0, // Valeur par défaut
          target: parseFloat(target),
          year: year,
          week: week,
          month: month,
          date: new Date(),
        });
        if (!ok) {
          toast.error("Error creating measurement with target");
          return;
        }
      }

      // Mettre à jour l'état local avec la nouvelle target
      setWeeklyKpis((prev) => {
        const newState = prev.map((kpi) => {
          if (kpi._id === kpiId) {
            const newStructure = [...(kpi.structure || [])];
            if (newStructure[weekIndex]) {
              newStructure[weekIndex] = { ...newStructure[weekIndex], target: parseFloat(target) };
            }
            return { ...kpi, structure: newStructure };
          }
          return kpi;
        });

        return newState;
      });

      toast.success("Target updated successfully");
    } catch (error) {
      console.error("❌ Error in updateTarget:", error);
      toast.error("Error updating target");
    }
  };

  useEffect(() => {
    if (user?._id) {
      setWeeklyDataStructure([]);
      fetchKpis();
    }
  }, [user, selectedYear, selectedMonth]);

  const handleCellEdit = (kpiId, weekIndex, type, currentValue) => {
    setEditingCell({ kpiId, weekIndex, type });
    setEditingValue(currentValue.toString());
  };

  const handleCellUpdate = async () => {
    if (!editingCell) return;

    const newValue = parseFloat(editingValue);
    if (isNaN(newValue)) {
      toast.error("Please enter a valid number");
      return;
    }

    if (editingCell.type === "value") {
      await updateMeasurement(editingCell.kpiId, editingCell.weekIndex, newValue);
    } else if (editingCell.type === "target") {
      await updateTarget(editingCell.kpiId, editingCell.weekIndex, newValue);
    }

    setEditingCell(null);
    setEditingValue("");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleCellUpdate();
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setEditingValue("");
    }
  };

  const handleDeleteKpi = async (kpiId) => {
    await deleteKpi(kpiId);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const months = [
    { value: 1, label: "Janvier" },
    { value: 2, label: "Février" },
    { value: 3, label: "Mars" },
    { value: 4, label: "Avril" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Juin" },
    { value: 7, label: "Juillet" },
    { value: 8, label: "Août" },
    { value: 9, label: "Septembre" },
    { value: 10, label: "Octobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Décembre" },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">KPIs Dashboard</h3>
        <button
          onClick={() => setShowAddKpi(!showAddKpi)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors">
          <FaPlus className="w-3 h-3" />
          Add KPI
        </button>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Année:</label>
          <Select options={years} value={selectedYear} onChange={setSelectedYear} getValue={(year) => year} getLabel={(year) => year} width="w-32" nullable={false} />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Mois:</label>
          <Select
            options={months}
            value={months.find((m) => m.value === selectedMonth)}
            onChange={(month) => setSelectedMonth(month.value)}
            getValue={(month) => month.value}
            getLabel={(month) => month.label}
            width="w-40"
            nullable={false}
          />
        </div>
      </div>

      {/* Add / Edit KPI Form */}
      {showAddKpi && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
          <div className="grid grid-cols-1 gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">KPI Name *</label>
                <input
                  type="text"
                  value={newKpiLabel}
                  onChange={(e) => setNewKpiLabel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter KPI name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Value *</label>
                <input
                  type="number"
                  value={newKpiTarget}
                  onChange={(e) => setNewKpiTarget(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter target"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newKpiDescription}
                onChange={(e) => setNewKpiDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter KPI description (optional)"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select
                  value={newKpiUnit}
                  onChange={(e) => setNewKpiUnit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="integer">Integer</option>
                  <option value="decimal">Decimal</option>
                  <option value="percentage">Percentage</option>
                  <option value="euro">Euro</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={editingKpiId ? submitEditKpi : addKpi}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors">
                  <FaCheck className="w-3 h-3 inline mr-1" />
                  {editingKpiId ? "Save changes" : "Add KPI"}
                </button>
                <button
                  onClick={() => {
                    setShowAddKpi(false);
                    setNewKpiLabel("");
                    setNewKpiDescription("");
                    setNewKpiTarget("");
                    setNewKpiUnit("integer");
                    setEditingKpiId(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-400 transition-colors">
                  <FaTimes className="w-3 h-3 inline mr-1" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-4 py-3 text-left text-sm font-medium text-gray-900">KPI Details</th>
              {weeklyDataStructure.map((week, index) => (
                <th key={index} className="border border-gray-300 px-2 py-3 text-center text-sm font-medium text-gray-900">
                  <div className="text-xs text-gray-500">
                    {week.startDate} - {week.endDate}
                  </div>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <div className="text-xs bg-blue-100 px-1 py-0.5 rounded">Value</div>
                    <div className="text-xs bg-green-100 px-1 py-0.5 rounded">Target</div>
                  </div>
                </th>
              ))}
              <th className="border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-900">Actions</th>
            </tr>
          </thead>
          <tbody>
            {weeklyKpis.map((kpi) => (
              <tr key={kpi._id} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-4 py-3">
                  <div className="text-sm font-medium text-gray-900">{kpi.name}</div>
                  <div className="text-xs text-gray-500 mt-1">Unit: {kpi.unit}</div>
                </td>
                {weeklyDataStructure.map((week, weekIndex) => (
                  <td key={weekIndex} className="border border-gray-300 px-2 py-3">
                    <div className="grid grid-cols-2 gap-1">
                      {/* Value Cell */}
                      <div className="text-center">
                        {editingCell?.kpiId === kpi._id && editingCell?.weekIndex === weekIndex && editingCell?.type === "value" ? (
                          <input
                            type="number"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            onBlur={handleCellUpdate}
                            className="w-full px-1 py-1 border border-blue-500 rounded text-xs text-center"
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => handleCellEdit(kpi._id, weekIndex, "value", kpi.weeklyData[weekIndex])}
                            className="px-1 py-1 text-xs cursor-pointer hover:bg-blue-50 rounded text-center bg-blue-50">
                            {kpi.weeklyData[weekIndex] || 0}
                          </div>
                        )}
                      </div>

                      {/* Target Cell */}
                      <div className="text-center">
                        {editingCell?.kpiId === kpi._id && editingCell?.weekIndex === weekIndex && editingCell?.type === "target" ? (
                          <input
                            type="number"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            onBlur={handleCellUpdate}
                            className="w-full px-1 py-1 border border-green-500 rounded text-xs text-center"
                            autoFocus
                          />
                        ) : (
                          <div
                            onClick={() => {
                              // Récupérer la target spécifique pour cette semaine de ce KPI
                              const kpiSpecificTarget = kpi.structure && kpi.structure[weekIndex] ? kpi.structure[weekIndex].target : null;
                              const displayTarget = kpiSpecificTarget !== null ? kpiSpecificTarget : kpi.target;
                              handleCellEdit(kpi._id, weekIndex, "target", displayTarget);
                            }}
                            className="px-1 py-1 text-xs cursor-pointer hover:bg-green-50 rounded text-center bg-green-50">
                            {(() => {
                              // Affichage de la target spécifique au KPI pour cette semaine
                              const kpiSpecificTarget = kpi.structure && kpi.structure[weekIndex] ? kpi.structure[weekIndex].target : null;
                              const displayValue = kpiSpecificTarget !== null ? kpiSpecificTarget : kpi.target;
                              return displayValue;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                ))}
                <td className="border border-gray-300 px-4 py-3 text-center">
                  <button onClick={() => handleDeleteKpi(kpi._id)} className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded" title="Delete KPI">
                    <FaTrash className="w-3 h-3" />
                  </button>
                  <button onClick={() => startEditKpi(kpi)} className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded" title="Update KPI">
                    <FaEdit className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {weeklyKpis.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>No KPIs yet. Add one above!</p>
        </div>
      )}
    </div>
  );
}
